import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import useCheckPC from 'hooks/useCheckPC';
import { getSortProperty } from 'utils/getSortProperty';
import ProductCard from 'components/ProductCard';
import { categoryArray } from 'components/ProductList';

const ProductGrid = ({ products, gridColumns, filter, sort, isProductDrawerOpen, isLoading, isShuffled }) => {
  const { isPC, isTabletLarge, isTabletSmall, isMobile } = useCheckPC();
  const [sortedProducts, setSortedProducts] = useState([]);
  const ulRef = useRef(null);
  const sortedUlRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      if (sortedUlRef.current) {
        await new Promise(resolve => setTimeout(resolve, 10));

        const titleItems = sortedUlRef.current.querySelectorAll('div > h2');
        addLoadedClassDelay(titleItems, 500, false);
      }
    };

    fetchData();
  }, [sort]);

  useEffect(() => {
    const getSortedProducts = sort => {
      return [...new Set(products.filter(product => product[sort] !== undefined).map(product => product[sort]))].sort();
    };

    let sortedArray;
    switch (sort) {
      case 'Categories':
        sortedArray = categoryArray;
        break;
      case 'Color':
        sortedArray = getSortedProducts('sort_color');
        break;
      case 'Material':
        sortedArray = getSortedProducts('sort_material');
        break;
      default:
        sortedArray = [];
    }
    setSortedProducts(sortedArray);
  }, [products, sort]);

  const addLoadedClassDelay = (items, delayTime, isListItem) => {
    const filteredItems = isListItem
      ? Array.from(items).filter(item => item.classList.contains('loaded'))
      : Array.from(items);

    filteredItems.forEach(item => {
      item.classList.remove('loaded');
      const delay = Number(item.style.transitionDelay.replace('ms', '')) + delayTime;

      setTimeout(() => {
        item.classList.add('loaded');
      }, delay);
    });
  };

  useLayoutEffect(() => {
    if (sort === 'Default') {
      if (ulRef.current) {
        const listItems = ulRef.current.children;
        addLoadedClassDelay(listItems, 300, true);
      }
    } else {
      if (sortedUlRef.current) {
        const listItems = sortedUlRef.current.querySelectorAll('div > ul > li');
        const titleItems = sortedUlRef.current.querySelectorAll('div > h2');
        addLoadedClassDelay(listItems, 300, true);
        addLoadedClassDelay(titleItems, 500, true);
      }
    }
  }, [isProductDrawerOpen, isShuffled]);

  const handleGrid = gridColumns => {
    const baseColumns = isPC ? 3 : isTabletLarge ? 3 : isTabletSmall ? 2 : 1;

    if (isProductDrawerOpen) {
      if (isPC) {
        if (gridColumns === 1) return parseInt((3 * baseColumns) / 2);
        else if (gridColumns === 2) return parseInt((2 * baseColumns) / 2);
        else return parseInt((4 * baseColumns) / 2);
      } else if (isTabletLarge) {
        if (gridColumns === 1) return parseInt((2 * baseColumns) / 2);
        else if (gridColumns === 2) return Math.ceil((1 * baseColumns) / 2);
        else return parseInt((3 * baseColumns) / 2);
      }
    } else {
      if (isPC) {
        if (gridColumns === 1) return 3 * baseColumns;
        else if (gridColumns === 2) return 2 * baseColumns;
        else return 4 * baseColumns;
      } else if (isTabletLarge) {
        if (gridColumns === 1) return 2 * baseColumns;
        else if (gridColumns === 2) return 1 * baseColumns;
        else return 3 * baseColumns;
      } else if (isTabletSmall) {
        if (gridColumns === 1) return 2 * baseColumns;
        else if (gridColumns === 2) return 1 * baseColumns;
        else return 3 * baseColumns;
      } else {
        if (gridColumns === 1) return 2 * baseColumns;
        else if (gridColumns === 2) return 1 * baseColumns;
        else return 3 * baseColumns;
      }
    }
  };

  const gridClassName = gridColumns => {
    if (handleGrid(gridColumns) === 12 || (handleGrid(gridColumns) === 6 && isProductDrawerOpen)) return 'small_grid';
    else if (handleGrid(gridColumns) === 9) return 'medium_grid';
    else if (handleGrid(gridColumns) === 6 || (handleGrid(gridColumns) === 3 && isProductDrawerOpen)) return 'big_grid';
    else if (handleGrid(gridColumns) === 4) return 'openDrawer_grid';
    else if (handleGrid(gridColumns) === 3) return 'grid_3';
    else if (handleGrid(gridColumns) === 2) return 'tiny_grid';
    else return 'products_grid';
  };

  const isSortedProducts = sortedProducts.length > 0;

  return (
    <>
      <ul
        style={{
          gridTemplateColumns: `repeat(${handleGrid(gridColumns)}, minmax(0, 1fr))`,
        }}
        className={`products ${gridClassName(gridColumns)} filtered_products ${isSortedProducts ? 'hidden' : ''}`}
        ref={ulRef}
      >
        {products
          .filter(product => filter === 'All' || filter === product.category)
          .map((product, i) => (
            <ProductCard
              key={`${gridColumns}-${product.image_src_1}`}
              product={product}
              isLoading={isLoading}
              isLast={i === products.length - 1}
              isProductDrawerOpen={isProductDrawerOpen}
            />
          ))}
      </ul>
      {/* sort products */}
      <div className={'sorted-products'} ref={sortedUlRef}>
        {sortedProducts.map((sortItem, i) => (
          <div key={i}>
            <h2>{sortItem}</h2>
            <ul
              style={{ gridTemplateColumns: `repeat(${handleGrid(gridColumns)}, minmax(0, 1fr))` }}
              className={`products ${gridClassName(gridColumns)}`}
            >
              {products
                .filter(product => product[getSortProperty(sort)] === sortItem)
                .map((product, index) => (
                  <ProductCard
                    key={`${gridColumns}-${product.image_src_1}`}
                    product={product}
                    isLoading={isLoading}
                    isSorted={true}
                    isProductDrawerOpen={isProductDrawerOpen}
                  />
                ))}
            </ul>
          </div>
        ))}
      </div>
    </>
  );
};
export default ProductGrid;
